import type { FC } from 'react';
import { ARROW_HEAD_SIZE, MESSAGE_FONT_SIZE, SELF_LOOP_HALF_HEIGHT, SELF_LOOP_WIDTH, estimateTextWidth, sequenceColors, } from './sequence-constants';
import { SequenceArrowKind } from './sequence-types';
import type { SequenceMessageLayout } from './layout-sequence';
import { SequenceText } from './SequenceText';
import { FontWeight } from '@/theme/typography';
const UNLABELED_VALUE = "unlabeled";
/** Marker ids for the two arrowhead styles (filled / open). Registered once in
 *  the parent <defs>; referenced here via url(). */
export const SEQUENCE_ARROW_FILLED_ID = 'cabal-seq-arrow-filled';
export const SEQUENCE_ARROW_OPEN_ID = 'cabal-seq-arrow-open';
const SELF_LABEL_GAP = 8;
const LABEL_BASELINE_LIFT = 6;
const SELF_LABEL_BASELINE = 4;
const LINE_WIDTH = 1.5;
const DASH_PATTERN = '5,4';
const MESSAGE_LABEL_HEIGHT = 18;
const MESSAGE_LABEL_PAD = 8;
interface SequenceMessageProps {
    message: SequenceMessageLayout;
}
function isDashedArrow(arrow: SequenceArrowKind): boolean {
    return arrow === SequenceArrowKind.Dashed || arrow === SequenceArrowKind.DashedOpen;
}
function isOpenArrow(arrow: SequenceArrowKind): boolean {
    return arrow === SequenceArrowKind.SolidOpen || arrow === SequenceArrowKind.DashedOpen;
}
/**
 * A single message line + label, mirroring FlowchartEdge's approach (an SVG
 * line/path with a marker-end arrowhead and a centered text label). Dashed
 * arrows set strokeDasharray; self-calls draw a small loop back to the lane.
 */
export const SequenceMessage: FC<SequenceMessageProps> = ({ message }) => {
    const { fromX, toX, y, arrow, text, selfCall } = message;
    const messageIdentity = `${message.fromId}-${message.toId}-${arrow}-${text || UNLABELED_VALUE}`;
    const markerId = isOpenArrow(arrow) ? SEQUENCE_ARROW_OPEN_ID : SEQUENCE_ARROW_FILLED_ID;
    const dash = isDashedArrow(arrow) ? DASH_PATTERN : undefined;
    const hasText = text.length > 0;
    if (selfCall) {
        // Self-message: a small rounded loop on the right of the lifeline.
        const top = y - SELF_LOOP_HALF_HEIGHT;
        const bottom = y + SELF_LOOP_HALF_HEIGHT;
        const path = `M ${fromX} ${top} h ${SELF_LOOP_WIDTH} v ${bottom - top} h ${-SELF_LOOP_WIDTH}`;
        return (
      <g>
        <path d={path} fill="none" stroke={sequenceColors.messageLine} strokeWidth={LINE_WIDTH} strokeDasharray={dash} markerEnd={`url(#${markerId})`}/>
        {hasText && (<SequenceText x={fromX + SELF_LOOP_WIDTH + SELF_LABEL_GAP} y={y + SELF_LABEL_BASELINE - MESSAGE_LABEL_HEIGHT + 3} width={estimateTextWidth(text, MESSAGE_FONT_SIZE) + MESSAGE_LABEL_PAD} height={MESSAGE_LABEL_HEIGHT} content={text} size={MESSAGE_FONT_SIZE} weight={FontWeight.Regular} color={sequenceColors.messageText} align="start"/>)}
      </g>
      );
    }
    const goingRight = toX >= fromX;
    // Stop the line a touch short of the lifeline so the arrowhead sits cleanly.
    const endX = goingRight ? toX - ARROW_HEAD_SIZE : toX + ARROW_HEAD_SIZE;
    const midX = (fromX + toX) / 2;
    return (
    <g>
      <line x1={fromX} y1={y} x2={endX} y2={y} stroke={sequenceColors.messageLine} strokeWidth={LINE_WIDTH} strokeDasharray={dash} markerEnd={`url(#${markerId})`}/>
      {hasText && (<SequenceText x={midX - (estimateTextWidth(text, MESSAGE_FONT_SIZE) + MESSAGE_LABEL_PAD) / 2} y={y - LABEL_BASELINE_LIFT - MESSAGE_LABEL_HEIGHT + 3} width={estimateTextWidth(text, MESSAGE_FONT_SIZE) + MESSAGE_LABEL_PAD} height={MESSAGE_LABEL_HEIGHT} content={text} size={MESSAGE_FONT_SIZE} weight={FontWeight.Regular} color={sequenceColors.messageText} align="center"/>)}
    </g>
    );
};
